/**
 * Onglets d'information produit (progressive enhancement).
 *
 * Sans ce script, la liste d'onglets est masquée par CSS et tous les panneaux
 * s'affichent empilés avec leur titre (dégradation lisible). À la connexion, le
 * composant pose l'attribut `data-enhanced` (le CSS bascule alors en mode onglets),
 * masque les panneaux inactifs et câble la navigation clavier (flèches / Home / End),
 * conformément au motif ARIA « tabs ».
 */
class ProductTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    if (this.tabs.length === 0 || this.tabs.length !== this.panels.length) return;

    this.setAttribute('data-enhanced', '');

    let active = this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
    if (active < 0) active = 0;

    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.#select(i));
      tab.addEventListener('keydown', (e) => this.#onKeydown(e, i));
    });

    this.#select(active);
  }

  #select(index) {
    this.tabs.forEach((tab, i) => {
      const on = i === index;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.tabIndex = on ? 0 : -1;
    });
    this.panels.forEach((panel, i) => {
      const on = i === index;
      panel.toggleAttribute('hidden', !on);
      panel.classList.toggle('is-active', on);
    });
  }

  #onKeydown(event, index) {
    const count = this.tabs.length;
    let target = -1;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        target = (index + 1) % count;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        target = (index - 1 + count) % count;
        break;
      case 'Home':
        target = 0;
        break;
      case 'End':
        target = count - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    this.#select(target);
    this.tabs[target].focus();
  }
}

if (!customElements.get('product-tabs')) {
  customElements.define('product-tabs', ProductTabs);
}
